import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { formatDate, formatRs } from '../lib/money';
import { openPrint } from '../lib/print';
import { handedRs, payrunApi, shortRs, type RunDetail, type RunSummary } from '../lib/payruns';
import PayRunPack from '../print/PayRunPack';

/**
 * Pay runs waiting for the owner, in his Approvals tab (25 September 2026:
 * "pay run approval should come to the approval window. however before
 * approve owner should [see] a summary").
 *
 * Each run is a line until he opens its summary — every employee's days,
 * gross, advances, EPF, loan and take-home, the totals, the cash needed and
 * what is due to the fund. Approve is only at the foot of that summary, so a
 * run cannot be approved unseen; Pay run history uses the same ReviewRun. The
 * full pack prints from there too. A run is a snapshot once created, so what
 * he reads is what he approves. Draws nothing when no run is waiting.
 */

const POLL_MS = 30_000;

export default function PayRunApprovals({ userName }: { userName: string }) {
  const [waiting, setWaiting] = useState<RunSummary[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await payrunApi.list();
      setWaiting(result.runs.filter((run) => run.status === 'unapproved'));
    } catch {
      // No signal: say nothing. Pay run history reports its own errors.
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (waiting.length === 0) {
    return done ? <div className="banner b-ok">{done}</div> : null;
  }

  return (
    <div className="card">
      <h3>Pay runs to approve</h3>
      <div className="sub">
        {waiting.length} waiting for you. Open the summary, check it, then approve — the office hands
        out the pay after that.
      </div>
      {done && <div className="banner b-ok">{done}</div>}

      {waiting.map((run) => (
        <div key={run.id}>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div className="name">
                {run.cycle === 'monthly' && 'Monthly pay · '}
                {formatDate(run.period_from)} – {formatDate(run.period_to)}
              </div>
              <div className="sub">
                {run.worker_count} employees · {formatRs(run.net_rs)} cash
                {run.loan_rs > 0 && ` · loans ${formatRs(run.loan_rs)}`}
                {' · '}prepared by {run.created_by_name}
              </div>
            </div>
            <button
              type="button"
              className={`btn sm${open === run.id ? '' : ' pri'}`}
              onClick={() => setOpen(open === run.id ? null : run.id)}
            >
              {open === run.id ? 'Hide summary' : 'Review summary'}
            </button>
          </div>
          {open === run.id && (
            <ReviewRun
              runId={run.id}
              userName={userName}
              onApproved={async (approved) => {
                setDone(
                  `Pay run ${formatDate(approved.period_from)} – ${formatDate(approved.period_to)} approved. ` +
                    'The office can hand out the pay and print payslips.',
                );
                setOpen(null);
                await load();
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * One run's summary with Approve at its foot. Loads the run itself, so both
 * Approvals and Pay run history can open it by id.
 */
export function ReviewRun({
  runId, userName, onApproved,
}: {
  runId: string;
  userName: string;
  onApproved: (run: RunDetail['run']) => Promise<void> | void;
}) {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    payrunApi.get(runId)
      .then((result) => { if (live) setDetail(result); })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'Could not open that pay run.');
      });
    return () => { live = false; };
  }, [runId]);

  async function approve() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await payrunApi.approve(detail.run.id);
      await onApproved(detail.run);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not approve that pay run.');
    } finally {
      setBusy(false);
    }
  }

  if (!detail) return <div className="empty">{error || 'Loading…'}</div>;

  const { run, rows } = detail;
  const fund = run.epf_employee_rs + run.epf_employer_rs + run.etf_rs;
  const hasLoans = run.loan_rs > 0;
  const hasAdvances = run.advances_rs > 0;
  const carriedIn = rows.reduce((sum, row) => sum + (row.carried_in_rs ?? 0), 0);
  const carriedOut = rows.reduce((sum, row) => sum + shortRs(row.take_home_rs), 0);

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '10px 12px', margin: '0 -4px 10px', flexBasis: '100%',
      }}
    >
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th className="num">Days</th>
              <th className="num">Gross</th>
              {hasAdvances && <th className="num">Advances</th>}
              <th className="num">EPF {run.epf_employee_pct_x100 / 100}%</th>
              {carriedIn > 0 && <th className="num">Short from last pay</th>}
              {hasLoans && <th className="num">Loan</th>}
              <th className="num">Take-home</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.worker_name}</td>
                <td className="num">{row.days_worked}</td>
                <td className="num">{formatRs(row.gross_rs)}</td>
                {hasAdvances && <td className="num">{row.advances_rs ? `−${formatRs(row.advances_rs)}` : '—'}</td>}
                <td className="num">{row.epf_employee_rs ? `−${formatRs(row.epf_employee_rs)}` : '—'}</td>
                {carriedIn > 0 && <td className="num">{row.carried_in_rs ? `−${formatRs(row.carried_in_rs)}` : '—'}</td>}
                {hasLoans && <td className="num">{row.loan_rs ? `−${formatRs(row.loan_rs)}` : '—'}</td>}
                <td className="num">
                  {formatRs(handedRs(row.take_home_rs))}
                  {shortRs(row.take_home_rs) > 0 && (
                    <div className="sub" style={{ color: 'var(--text-warning)' }}>
                      short {formatRs(shortRs(row.take_home_rs))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>{rows.length} employees</th>
              <th className="num">{rows.reduce((sum, row) => sum + row.days_worked, 0)}</th>
              <th className="num">{formatRs(run.gross_rs)}</th>
              {hasAdvances && <th className="num">−{formatRs(run.advances_rs)}</th>}
              <th className="num">{run.epf_employee_rs ? `−${formatRs(run.epf_employee_rs)}` : '—'}</th>
              {carriedIn > 0 && <th className="num">−{formatRs(carriedIn)}</th>}
              {hasLoans && <th className="num">−{formatRs(run.loan_rs)}</th>}
              <th className="num">{formatRs(run.net_rs)}</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="ln tot" style={{ marginTop: 8 }}>
        <span>Cash needed to pay the employees</span>
        <span>{formatRs(run.net_rs)}</span>
      </div>
      {hasAdvances && (
        <div className="ln">
          <span className="k">Advances already paid out during the week (taken off above)</span>
          <span>{formatRs(run.advances_rs)}</span>
        </div>
      )}
      {hasLoans && (
        <div className="ln">
          <span className="k">Loan repayments kept back (taken off above)</span>
          <span>{formatRs(run.loan_rs)}</span>
        </div>
      )}
      {carriedIn > 0 && (
        <div className="ln">
          <span className="k">Short from the last pay, taken back (taken off above)</span>
          <span>{formatRs(carriedIn)}</span>
        </div>
      )}
      {carriedOut > 0 && (
        <div className="ln">
          <span className="k">Advances more than the pay — carried to the next pay</span>
          <span style={{ color: 'var(--text-warning)' }}>{formatRs(carriedOut)}</span>
        </div>
      )}
      {fund > 0 && (
        <div className="ln">
          <span className="k">
            Due to EPF/ETF: employee {formatRs(run.epf_employee_rs)} + employer {formatRs(run.epf_employer_rs)} + ETF {formatRs(run.etf_rs)}
          </span>
          <span>{formatRs(fund)}</span>
        </div>
      )}
      <div className="sub" style={{ marginTop: 6 }}>
        {run.cycle === 'monthly' ? 'Monthly, ' : ''}{formatDate(run.period_from)} – {formatDate(run.period_to)}
        {run.pay_on ? ` · pay day ${formatDate(run.pay_on)}` : ''} · prepared by {run.created_by_name} on{' '}
        {new Date(run.created_at).toLocaleString('en-GB')}.
      </div>

      {error && <div className="banner b-no">{error}</div>}

      <div className="bar" style={{ marginTop: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn sm"
          onClick={() =>
            openPrint(
              `Unapproved pay run — summary + ${rows.length} payslips`,
              <PayRunPack detail={detail} printedBy={userName} printedAt={new Date()} />,
            )
          }
        >
          Print for checking
        </button>
        {run.status === 'unapproved' && (
          <button type="button" className="btn pri" disabled={busy} onClick={() => void approve()}>
            {busy ? 'Approving…' : `Approve pay run — ${formatRs(run.net_rs)}`}
          </button>
        )}
      </div>
    </div>
  );
}
